import { Menu } from 'lucide-react';
import dynamic from 'next/dynamic';

import { Link } from '@natu/next-link';
import { Accordion } from '@natu/ui/Accordion';
import { Button } from '@natu/ui/Button';
import { NavigationMenu, NavigationMenuList } from '@natu/ui/NavigationMenu';
import { ScrollArea } from '@natu/ui/ScrollArea';
import { Sheet, SheetContent, SheetHeader, SheetTrigger } from '@natu/ui/Sheet';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import {
  BlokItem,
  SBProps,
  StoryblokAsset,
  StoryblokLink,
  getAssetFromStoryblok,
  sbEditable,
} from '../../../../utils';
import { NavigationButton } from './components/NavigationButton';

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

const DynamicRender = dynamic(() => import('../../../../utils').then(mod => mod.DynamicRender));

interface SBNavigationProps {
  body?: BlokItem[];
  socialItems?: BlokItem[];
  mobileAccordionType?: 'multiple' | 'single';
  fullWidth?: boolean;
  hamburgerPosition?: 'left' | 'right';
  rightLink?: StoryblokLink;
  mobileLogo?: StoryblokAsset;
  rightLinkLabel?: string;
  rightLinkAddEvent?: boolean;
}

export const SBNavigation = ({ blok }: SBProps<SBNavigationProps>) => {
  const {
    body,
    socialItems,
    mobileAccordionType,
    rightLinkLabel,
    rightLink,
    mobileLogo,
    rightLinkAddEvent,
  } = blok;

  const logoImageProps = getAssetFromStoryblok(mobileLogo, { type: 'image' });

  return (
    <>
      <NavigationMenu navigationMenuViewportClassName="left-auto" {...sbEditable(blok)}>
        <Accordion
          type={mobileAccordionType || 'single'}
          collapsible={mobileAccordionType === 'single' ? true : undefined}
        >
          {/* Desktop navigation  */}
          <NavigationMenuList className="mr-auto hidden gap-6 lg:flex xl:gap-10 [&>a]:text-lg [&>a]:lg:text-base [&>li>button]:lg:p-0">
            <DynamicRender data={body} />
          </NavigationMenuList>

          {/* Mobile navigation  */}
          <Sheet modal>
            <SheetTrigger asChild>
              <Button size="icon" variant="ghost" className="lg:hidden">
                <Menu />
                <span className="sr-only">Navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full gap-0 px-0 pb-8 sm:max-w-xl" side="right">
              <SheetHeader className="h-[72px] px-4">
                {logoImageProps.src && (
                  <Link href="/" className="max-w-max">
                    <ResponsiveImage
                      className="h-8 w-24"
                      width={96}
                      height={32}
                      {...logoImageProps}
                    />
                    <span className="sr-only">Home page</span>
                  </Link>
                )}
              </SheetHeader>
              <ScrollArea className="h-[calc(100%-88px)] px-4">
                <div className="flex flex-col gap-6">
                  <DynamicRender data={body} />
                </div>
                <NavigationButton
                  link={rightLink}
                  label={rightLinkLabel}
                  hasEvent={rightLinkAddEvent}
                  isMobile
                />
                {isArrayWithLength(socialItems) && (
                  <div className="mt-8 flex items-center justify-center gap-1 text-gray-300">
                    <DynamicRender data={socialItems} />
                  </div>
                )}
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </Accordion>
      </NavigationMenu>
      <NavigationButton link={rightLink} label={rightLinkLabel} hasEvent={rightLinkAddEvent} />
    </>
  );
};
